// Intelligent navbar search — instant, grouped, keyboard-navigable typeahead.
//
// Powered by ikart.webshop.api.search_suggest (one endpoint, audience-aware:
// products + categories + brands everywhere, dealers on the marketplace). The
// dropdown groups results, supports full keyboard navigation, remembers recent
// searches, and always FAILS OPEN — if the endpoint errors the panel just
// hides and the plain <form> submit still takes the user to the results page.
//
// Bound to the navbar #search-box by init.js (and reused by the in-page product
// view). Namespaced ".imek-suggest" events + an idempotent bind make re-binding
// on the same box a no-op.
ikart.webshop.product_ui.ProductSearch = class {
	constructor(opts) {
		$.extend(this, opts || {});
		this.search_box_id = this.search_box_id || "#search-box";
		this.$box = $(this.search_box_id);
		if (!this.$box.length) return;

		this.$wrapper = $("#dropdownMenuSearch");
		this.$form = this.$box.closest("form");
		this.minChars = 2;
		this.recentKey = "imek_recent_searches";
		this.cityKey = "imek_mkt_city";
		this.activeIndex = -1;
		this.lastQuery = null;
		this.reqSeq = 0;

		this.buildPanel();
		this.bind();
	}

	buildPanel() {
		// Keep the #search-results-container id: legacy CSS + the click-outside
		// guard elsewhere key off it. The rich markup lives inside.
		this.$panel = this.$wrapper.find("#search-results-container");
		if (!this.$panel.length) {
			this.$panel = $(
				'<div id="search-results-container" class="imek-suggest-panel hidden" role="listbox" ' +
					'aria-label="' + __("Search suggestions") + '" aria-live="polite"></div>'
			);
			this.$wrapper.append(this.$panel);
		}
		this.$panel.addClass("imek-suggest-panel");
	}

	bind() {
		const me = this;

		this.debounced = frappe.utils.debounce(function (q) {
			me.fetch(q);
		}, 180);

		this.$box
			.off(".imek-suggest")
			.attr("autocomplete", "off")
			.on("input.imek-suggest", function () {
				const q = (this.value || "").trim();
				if (q.length < me.minChars) {
					me.lastQuery = null;
					me.showRecent();
					return;
				}
				me.renderLoading();
				me.open();
				me.debounced(q);
			})
			.on("focus.imek-suggest", function () {
				const q = (this.value || "").trim();
				if (q.length >= me.minChars) {
					me.open();
				} else {
					me.showRecent();
				}
			})
			.on("keydown.imek-suggest", function (e) {
				me.onKeydown(e);
			});

		// Remember the term whenever the form is actually submitted.
		if (this.$form.length) {
			this.$form.on("submit.imek-suggest", function () {
				me.remember((me.$box.val() || "").trim());
			});
		}

		// Close when focus/click leaves the search area.
		$(document)
			.off("click.imek-suggest-" + this._uid())
			.on("click.imek-suggest-" + this._uid(), function (e) {
				if (!$(e.target).closest("#dropdownMenuSearch").length) me.close();
			});
	}

	_uid() {
		if (!this.__uid) this.__uid = Math.random().toString(36).slice(2);
		return this.__uid;
	}

	// ---- data ----------------------------------------------------------
	fetch(query) {
		const me = this;
		const seq = ++this.reqSeq;
		let city = "";
		try {
			city = localStorage.getItem(this.cityKey) || "";
		} catch (e) {
			/* private mode */
		}
		frappe.call({
			method: "ikart.webshop.api.search_suggest",
			args: { query: query, city: city },
			callback: function (r) {
				// Ignore out-of-order responses and stale queries.
				if (seq !== me.reqSeq) return;
				if ((me.$box.val() || "").trim() !== query) return;
				me.lastQuery = query;
				me.render(r.message || {});
			},
			error: function () {
				if (seq === me.reqSeq) me.close(); // fail open to form submit
			},
		});
	}

	// ---- rendering -----------------------------------------------------
	esc(s) {
		return frappe.utils.escape_html(s == null ? "" : String(s));
	}

	render(data) {
		const products = data.products || [];
		const categories = data.categories || [];
		const brands = data.brands || [];
		const dealers = data.dealers || [];
		const q = data.query || (this.$box.val() || "").trim();

		if (!products.length && !categories.length && !brands.length && !dealers.length) {
			this.renderEmpty(q);
			return;
		}

		let html = "";

		if (products.length) {
			html += '<div class="imek-suggest-group">';
			html += '<div class="imek-suggest-group-title">' + __("Products") + "</div>";
			html += products.map((p) => this.productRow(p)).join("");
			html += "</div>";
		}

		if (categories.length) {
			html += '<div class="imek-suggest-group">';
			html += '<div class="imek-suggest-group-title">' + __("Categories") + "</div>";
			html += '<div class="imek-suggest-chips">';
			html += categories.map((c) => this.chip(c.url, c.label)).join("");
			html += "</div></div>";
		}

		if (brands.length) {
			html += '<div class="imek-suggest-group">';
			html += '<div class="imek-suggest-group-title">' + __("Brands") + "</div>";
			html += '<div class="imek-suggest-chips">';
			html += brands.map((b) => this.chip(b.url, b.label)).join("");
			html += "</div></div>";
		}

		if (dealers.length) {
			html += '<div class="imek-suggest-group">';
			html += '<div class="imek-suggest-group-title">' + __("Dealers") + "</div>";
			html += dealers.map((d) => this.dealerRow(d)).join("");
			html += "</div>";
		}

		html += this.seeAll(q);

		this.$panel.html(html);
		this.activeIndex = -1;
		this.open();
	}

	productRow(p) {
		const img = p.image
			? '<img class="imek-suggest-thumb" src="' + encodeURI(p.image) + '" loading="lazy" alt="">'
			: '<span class="imek-suggest-thumb imek-suggest-thumb-empty" aria-hidden="true">' +
				this.esc((p.title || "?").charAt(0).toUpperCase()) +
				"</span>";
		const sub = p.brand ? '<span class="imek-suggest-sub">' + this.esc(p.brand) + "</span>" : "";
		return (
			'<a class="imek-suggest-item" role="option" href="' + this.esc(p.url || "#") + '">' +
			img +
			'<span class="imek-suggest-text"><span class="imek-suggest-name">' +
			this.esc(p.title) +
			"</span>" +
			sub +
			"</span></a>"
		);
	}

	dealerRow(d) {
		const img = d.image
			? '<img class="imek-suggest-thumb imek-suggest-thumb-round" src="' + encodeURI(d.image) + '" loading="lazy" alt="">'
			: '<span class="imek-suggest-thumb imek-suggest-thumb-round imek-suggest-thumb-empty" aria-hidden="true">' +
				this.esc((d.label || "?").charAt(0).toUpperCase()) +
				"</span>";
		const city = d.city ? '<span class="imek-suggest-sub">' + this.esc(d.city) + "</span>" : "";
		return (
			'<a class="imek-suggest-item" role="option" href="' + this.esc(d.url || "#") + '">' +
			img +
			'<span class="imek-suggest-text"><span class="imek-suggest-name">' +
			this.esc(d.label) +
			"</span>" +
			city +
			"</span></a>"
		);
	}

	chip(url, label) {
		return '<a class="imek-suggest-chip" role="option" href="' + this.esc(url || "#") + '">' + this.esc(label) + "</a>";
	}

	seeAll(q) {
		return (
			'<a class="imek-suggest-seeall" role="option" href="' +
			this.esc(this.resultsUrl(q)) +
			'"><span>' +
			__("See all results for “{0}”", [this.esc(q)]) +
			'</span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
			'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
			'<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></a>'
		);
	}

	resultsUrl(q) {
		const action = (this.$form.attr("action") || "/products").split("?")[0];
		return action + "?search=" + encodeURIComponent(q || "");
	}

	renderLoading() {
		this.$panel.html('<div class="imek-suggest-status">' + __("Searching…") + "</div>");
		this.open();
	}

	renderEmpty(q) {
		this.$panel.html(
			'<div class="imek-suggest-empty">' +
				"<strong>" + __("No matches for “{0}”", [this.esc(q)]) + "</strong>" +
				"<span>" + __("Try a different keyword or check the spelling.") + "</span>" +
				this.seeAll(q) +
				"</div>"
		);
		this.activeIndex = -1;
		this.open();
	}

	// ---- recent searches ----------------------------------------------
	getRecent() {
		try {
			const raw = JSON.parse(localStorage.getItem(this.recentKey) || "[]");
			return Array.isArray(raw) ? raw.filter((s) => typeof s === "string") : [];
		} catch (e) {
			return [];
		}
	}

	remember(term) {
		term = (term || "").trim();
		if (term.length < this.minChars) return;
		let list = this.getRecent().filter((s) => s.toLowerCase() !== term.toLowerCase());
		list.unshift(term);
		list = list.slice(0, 6);
		try {
			localStorage.setItem(this.recentKey, JSON.stringify(list));
		} catch (e) {
			/* private mode */
		}
	}

	showRecent() {
		const list = this.getRecent();
		if (!list.length) {
			this.close();
			return;
		}
		const me = this;
		let html = '<div class="imek-suggest-group">';
		html +=
			'<div class="imek-suggest-group-title imek-suggest-recent-title">' +
			__("Recent searches") +
			'<button type="button" class="imek-suggest-recent-clear">' + __("Clear") + "</button></div>";
		html += '<div class="imek-suggest-chips">';
		html += list
			.map(
				(t) =>
					'<button type="button" class="imek-suggest-chip imek-suggest-recent-chip" role="option">' +
					me.esc(t) +
					"</button>"
			)
			.join("");
		html += "</div></div>";
		this.$panel.html(html);
		this.activeIndex = -1;
		this.open();

		this.$panel.find(".imek-suggest-recent-chip").on("click", function () {
			const term = $(this).text();
			me.$box.val(term).trigger("focus");
			me.renderLoading();
			me.debounced(term);
		});
		this.$panel.find(".imek-suggest-recent-clear").on("click", function (e) {
			e.stopPropagation();
			try {
				localStorage.removeItem(me.recentKey);
			} catch (err) {
				/* private mode */
			}
			me.close();
		});
	}

	// ---- open / close / keyboard --------------------------------------
	open() {
		this.$panel.removeClass("hidden");
		this.$box.attr("aria-expanded", "true");
	}

	close() {
		this.$panel.addClass("hidden");
		this.$box.attr("aria-expanded", "false");
		this.activeIndex = -1;
		this.clearActive();
	}

	items() {
		return this.$panel.find(".imek-suggest-item, .imek-suggest-chip, .imek-suggest-seeall").toArray();
	}

	clearActive() {
		this.$panel.find(".is-active").removeClass("is-active");
	}

	highlight(i) {
		const items = this.items();
		if (!items.length) return;
		this.clearActive();
		if (i < 0) i = items.length - 1;
		if (i >= items.length) i = 0;
		this.activeIndex = i;
		const el = items[i];
		el.classList.add("is-active");
		if (el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
	}

	onKeydown(e) {
		const open = !this.$panel.hasClass("hidden");
		if (e.key === "ArrowDown") {
			if (!open) {
				this.$box.trigger("focus");
				return;
			}
			e.preventDefault();
			this.highlight(this.activeIndex + 1);
		} else if (e.key === "ArrowUp") {
			if (!open) return;
			e.preventDefault();
			this.highlight(this.activeIndex - 1);
		} else if (e.key === "Enter") {
			const items = this.items();
			if (open && this.activeIndex >= 0 && items[this.activeIndex]) {
				// Links navigate; recent-search chips are buttons and must activate
				// their own fetch handler rather than assigning a null href.
				e.preventDefault();
				const item = items[this.activeIndex];
				const href = item.getAttribute("href");
				if (href) {
					this.remember((this.$box.val() || "").trim());
					window.location.href = href;
				} else {
					item.click();
				}
			}
			// else: let the form submit normally (handled by submit.imek-suggest)
		} else if (e.key === "Escape") {
			this.close();
		}
	}
};
